import { useState } from "react";
import { Send, BriefcaseBusiness, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { sendApplication } from "@/lib/api";

const ROLES = [
    "Auxiliar de Limpeza",
    "Manutenção Predial",
    "Vigia",
    "Porteiro / Controle de Acesso",
    "Recepcionista",
    "Auxiliar Administrativo",
    "Copeiro / Apoio a Eventos",
    "Outro",
];

export default function TrabalheConosco() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        role: ROLES[0],
        city: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.role) {
            toast.error("Preencha os campos obrigatórios.");
            return;
        }
        setLoading(true);
        try {
            await sendApplication(form);
            setSuccess(true);
            toast.success("Candidatura enviada! Entraremos em contato.");
            setForm({ name: "", email: "", phone: "", role: ROLES[0], city: "", message: "" });
        } catch (err) {
            toast.error("Não foi possível enviar. Tente novamente.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="trabalhe-conosco"
            data-testid="trabalhe-conosco-section"
            className="relative py-24 sm:py-32 border-t border-zinc-200 dark:border-white/5 bg-zinc-50 dark:bg-[#070707]"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <span className="inline-block text-xs font-bold uppercase tracking-[0.22em] text-[#E89546]">
                        Trabalhe Conosco
                    </span>
                    <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-zinc-950 dark:text-white leading-tight">
                        Construa carreira em uma operação séria.
                    </h2>
                    <p className="mt-6 text-zinc-700 dark:text-zinc-300 leading-relaxed">
                        Buscamos profissionais comprometidos, com postura ética e vontade de crescer. Cadastre seus dados e nosso RH entrará em contato sempre que houver oportunidade compatível com o seu perfil.
                    </p>

                    <div className="mt-10 space-y-4">
                        {[
                            "Equipe treinada e suporte operacional",
                            "Processos claros e bem definidos",
                            "Oportunidades em diversos segmentos",
                        ].map((t) => (
                            <div key={t} className="flex items-start gap-3 text-zinc-700 dark:text-zinc-300">
                                <CheckCircle2 className="w-5 h-5 text-[#2DA89E] shrink-0 mt-0.5" />
                                <span>{t}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <form
                        data-testid="trabalhe-form"
                        onSubmit={onSubmit}
                        className="rounded-3xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0d0d0d] p-6 sm:p-10 shadow-sm dark:shadow-none"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-10 h-10 rounded-xl bg-[#E89546]/10 text-[#E89546] flex items-center justify-center">
                                <BriefcaseBusiness className="w-5 h-5" />
                            </span>
                            <h3 className="font-heading text-xl font-bold text-zinc-950 dark:text-white">
                                Cadastre seu currículo
                            </h3>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Nome completo *" name="name" value={form.name} onChange={onChange} testid="trabalhe-input-name" />
                            <Field label="E-mail *" type="email" name="email" value={form.email} onChange={onChange} testid="trabalhe-input-email" />
                            <Field label="Telefone / WhatsApp *" name="phone" value={form.phone} onChange={onChange} testid="trabalhe-input-phone" />
                            <Field label="Cidade" name="city" value={form.city} onChange={onChange} testid="trabalhe-input-city" />

                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-2">
                                    Cargo de interesse *
                                </label>
                                <select
                                    data-testid="trabalhe-select-role"
                                    name="role"
                                    value={form.role}
                                    onChange={onChange}
                                    className="w-full bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-xl px-4 py-3 text-zinc-950 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E89546] focus:border-[#E89546] transition"
                                >
                                    {ROLES.map((r) => (
                                        <option key={r} value={r} className="bg-white dark:bg-[#0d0d0d] text-zinc-950 dark:text-white">
                                            {r}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-2">
                                    Mensagem (opcional)
                                </label>
                                <textarea
                                    data-testid="trabalhe-input-message"
                                    name="message"
                                    rows={4}
                                    value={form.message}
                                    onChange={onChange}
                                    placeholder="Conte-nos brevemente sobre sua experiência..."
                                    className="w-full bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-xl px-4 py-3 text-zinc-950 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-[#E89546] focus:border-[#E89546] transition resize-none"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                            <button
                                data-testid="trabalhe-submit-btn"
                                type="submit"
                                disabled={loading}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#E89546] text-black font-semibold transition-transform hover:bg-[#f0a25b] active:scale-95 disabled:opacity-60"
                            >
                                {loading ? "Enviando..." : (<>Enviar candidatura <Send className="w-4 h-4" /></>)}
                            </button>
                            {success && (
                                <span data-testid="trabalhe-success" className="text-sm text-[#2DA89E] inline-flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4" /> Recebemos sua candidatura.
                                </span>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, name, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-2">{label}</label>
            <input
                data-testid={testid}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                className="w-full bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-xl px-4 py-3 text-zinc-950 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-[#E89546] focus:border-[#E89546] transition"
            />
        </div>
    );
}
